import React from "react";
import { AccessItem } from "components/shareSection/AccessItem";
import { PermissionControl } from "components/shareSection/PermissionControl";
import { PendingInviteRow } from "components/shareSection/PendingInviteRow";
import { EntityType, ResourceType } from "components/shareSection/entities";
import { TeamMember } from "./TeamsApi";

export type TeamMemberRowMode =
  | { kind: "readonly" }
  | {
      kind: "interactive";
      isAdmin: boolean;
      isUpdating: boolean;
      showSuccess: boolean;
      onUpdateAccess: (member: TeamMember, newAccess: string) => Promise<void>;
      onRemoveMember: (member: TeamMember) => Promise<void>;
      onResendInvitation: (email: string) => Promise<void>;
    };

interface TeamMemberRowProps {
  member: TeamMember;
  mode: TeamMemberRowMode;
  className?: string;
}

export const TeamMemberRow: React.FC<TeamMemberRowProps> = ({
  member,
  mode,
  className,
}) => {
  const userEmail = String(member.user.user_email_js() || "");
  const userName = String(member.user.user_name_js() || "");
  const isCurrentUser = !member.isPending && member.isCurrentUser;

  if (member.isPending && mode.kind === "interactive") {
    return (
      <PendingInviteRow
        email={userEmail}
        canRevokeAccess={mode.isAdmin}
        onResend={
          mode.isAdmin ? () => mode.onResendInvitation(userEmail) : undefined
        }
        onRevoke={mode.isAdmin ? () => mode.onRemoveMember(member) : undefined}
      />
    );
  }

  const primaryText = member.isPending
    ? userEmail
    : userName || userEmail || "Unknown User";

  return (
    <AccessItem
      primaryText={primaryText}
      tooltipText={userEmail || userName || undefined}
      isCurrentUser={isCurrentUser}
      isPending={member.isPending}
      permissionLabel={renderPermissionLabel(member, mode, isCurrentUser)}
      canRevokeAccess={false}
      className={className}
    />
  );
};

function renderPermissionLabel(
  member: TeamMember,
  mode: TeamMemberRowMode,
  isCurrentUser: boolean,
): React.ReactNode {
  if (mode.kind === "readonly") {
    return (
      <span className="text-fg-muted-medium shrink-0">
        {member.isPending ? "Invited" : member.accessLevel}
      </span>
    );
  }
  return (
    <PermissionControl
      currentAccess={member.accessLevel}
      resourceType={ResourceType.Model}
      isDisabled={!mode.isAdmin || isCurrentUser}
      isUpdating={mode.isUpdating}
      isCurrentUser={isCurrentUser}
      showSuccess={mode.showSuccess}
      onAccessChange={
        mode.isAdmin && !isCurrentUser
          ? (newAccess) => mode.onUpdateAccess(member, newAccess)
          : undefined
      }
      onRemove={
        mode.isAdmin && !isCurrentUser
          ? () => mode.onRemoveMember(member)
          : undefined
      }
      entityType={EntityType.Team}
      removeLabel="Remove from Team"
      skipRemoveConfirmation
    />
  );
}
